import React from 'react';
import SEO from '../../components/common/SEO';
import { buildCanonicalUrl } from '../../utils/seoUtils';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Terms of Service"
        description="Review the KL Accelerators Terms of Service. Understand the terms governing your use of our enterprise accelerator platform and related services."
        canonicalUrl={buildCanonicalUrl('/terms-of-service')}
        keywords="terms of service, user agreement, KL Accelerators"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          <div className="text-sm text-gray-600 mb-8">
            <p><strong>Last Updated:</strong> December 2024</p>
            <p><strong>Effective Date:</strong> January 1, 2025</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing or using KL Accelerators' services, you agree to be bound by these Terms of Service 
              ("Terms"). If you do not agree to these Terms, do not use our services.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Description of Services</h2>
            <p className="text-gray-700 mb-4">
              KL Accelerators provides ready-to-deploy accelerators and development tools designed for rapid enterprise adoption, including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Credit analysis and risk assessment tools</li>
              <li>Document processing and verification services</li>
              <li>Fraud detection and prevention systems</li>
              <li>Compliance monitoring and reporting tools</li>
              <li>Custom AI agent development services</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. User Accounts and Eligibility</h2>
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">3.1 Eligibility</h3>
            <p className="text-gray-700 mb-4">You must be:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>At least 18 years of age</li>
              <li>A licensed mortgage professional or authorized representative</li>
              <li>Legally able to enter into binding contracts</li>
              <li>Compliant with all applicable laws and regulations</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">3.2 Account Security</h3>
            <p className="text-gray-700 mb-4">You are responsible for:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of unauthorized access</li>
              <li>Providing accurate and up-to-date information</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Pricing and Payment</h2>
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">4.1 Pricing Model</h3>
            <p className="text-gray-700 mb-4">
              Our services operate on an outcome-based pricing model with zero upfront costs. You pay only 
              when our AI agents successfully process transactions (per loan, per property, or per order).
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">4.2 Payment Terms</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Payments are due within 30 days of invoice date</li>
              <li>All fees are non-refundable unless otherwise specified</li>
              <li>Late payments may incur additional charges</li>
              <li>Pricing may change with 30 days written notice</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree NOT to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Use our services for illegal or unauthorized purposes</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Interfere with or disrupt our services</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Share or resell our services without permission</li>
              <li>Input false, misleading, or fraudulent information</li>
              <li>Reverse engineer or copy our proprietary technology</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Data and Privacy</h2>
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">6.1 Data Processing</h3>
            <p className="text-gray-700 mb-4">
              You grant us permission to process data you submit through our services for the purposes of 
              providing AI analysis, compliance checking, and service improvement.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">6.2 Data Security</h3>
            <p className="text-gray-700 mb-4">
              We implement industry-standard security measures to protect your data. However, you acknowledge 
              that no system is completely secure, and you use our services at your own risk.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Intellectual Property</h2>
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">7.1 Our IP</h3>
            <p className="text-gray-700 mb-4">
              All AI models, algorithms, software, and content provided by Ultralender.ai remain our exclusive 
              intellectual property. You receive only a limited license to use our services.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">7.2 Your Data</h3>
            <p className="text-gray-700 mb-4">
              You retain ownership of your input data. You grant us a license to use your data solely for 
              providing our services and improving our AI models (in anonymized form).
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Disclaimers and Limitations</h2>
            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">8.1 Service Disclaimers</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Our AI services are tools to assist decision-making, not replace professional judgment</li>
              <li>You remain responsible for all final decisions and compliance</li>
              <li>We do not guarantee specific outcomes or results</li>
              <li>Services are provided "as is" without warranties</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mt-6 mb-3">8.2 Limitation of Liability</h3>
            <p className="text-gray-700 mb-4">
              Our liability is limited to the fees paid by you in the 12 months preceding the claim. We are not 
              liable for indirect, consequential, or punitive damages.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Regulatory Compliance</h2>
            <p className="text-gray-700 mb-4">
              You acknowledge that mortgage industry regulations apply to your use of our services. You remain 
              responsible for compliance with all applicable laws, including but not limited to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Fair Credit Reporting Act (FCRA)</li>
              <li>Equal Credit Opportunity Act (ECOA)</li>
              <li>Truth in Lending Act (TILA)</li>
              <li>Real Estate Settlement Procedures Act (RESPA)</li>
              <li>Qualified Mortgage (QM) rules</li>
              <li>State and local regulations</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Termination</h2>
            <p className="text-gray-700 mb-4">
              Either party may terminate this agreement with 30 days written notice. We may terminate immediately 
              for breach of these Terms. Upon termination, you must cease using our services and delete any 
              downloaded materials.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms are governed by the laws of the United States and the state where our principal office 
              is located, without regard to conflict of law principles.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We may modify these Terms at any time. Material changes will be communicated through our website 
              or email. Continued use of our services constitutes acceptance of updated Terms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">13. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms, contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Email:</strong> contact@kadellabs.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;